import { CustomAxiosRequestConfig, http } from '../../http';
import {
    DashboardAccessLevelFilter,
    RequestCopyDashboard,
    RequestDeleteDashboard,
    RequestGetDashboard,
    RequestGetDashboardList,
    RequestPatchDashboardFavorite,
    RequestPatchDashboardLayout,
    RequestPatchDashboardSortOrder,
    RequestPatchDashboardTitle,
    RequestPostDashboard,
    ResponseDeleteDashboard,
    ResponseGetDashboard,
    ResponseGetDashboardList,
    ResponsePatchDashboardFavorite,
    ResponsePatchDashboardLayout,
    ResponsePatchDashboardSortOrder,
    ResponsePatchDashboardTitle,
    ResponsePostDashboard,
} from './types';

import { __ } from '@/js/stores/expressions';

export const getDashboardsList = async (accessLevel?: DashboardAccessLevelFilter) => {
    const params = accessLevel ? { accessLevel } : undefined;
    return http
        .get<RequestGetDashboardList, ResponseGetDashboardList>('dashboard/list', { params })
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error fetching dashboard list:', error);
            throw error;
        });
};

export const getDashboardById = async (id: string, signal?: AbortSignal) => {
    const config: CustomAxiosRequestConfig = {
        signal,
        errorMessage: {
            404: __('error-404-dashboard-item', { dashboardId: id }),
        },
    };

    return http
        .get<RequestGetDashboard, ResponseGetDashboard>(`dashboard/${id}`, config)
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error fetching dashboard by ID:', error);
            throw error;
        });
};

export const addDashboard = async (req: RequestPostDashboard) => {
    const config: CustomAxiosRequestConfig = {
        successMessage: __('new-dashboard-success'),
    };

    return http
        .post<RequestPostDashboard, ResponsePostDashboard>(
            'dashboard',
            {
                title: req.title,
            },
            config
        )
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error adding dashboard:', error);
            throw error;
        });
};

export const copyDashboard = async (req: RequestCopyDashboard) => {
    const config: CustomAxiosRequestConfig = {
        successMessage: __('copy-dashboard-success'),
        errorMessage: {
            404: __('copy-dashboard-error-response-404', { dashboardId: req.dashboardId }),
        },
    };
    return http
        .post<RequestCopyDashboard, ResponsePostDashboard>(
            `dashboard/${req.dashboardId}/copy`,
            {
                title: req.title,
            },
            config
        )
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error copying dashboard:', error);
            throw error;
        });
};

export const updateDashboardTitle = async (newTitle: string, dashboardId: string) => {
    const config: CustomAxiosRequestConfig = {
        successMessage: __('update-dashboard-title-success'),
        errorMessage: {
            404: __('update-dashboard-title-error-response-404', { dashboardId }),
        },
    };

    return http
        .patch<RequestPatchDashboardTitle, ResponsePatchDashboardTitle>(
            `dashboard/${dashboardId}/title`,
            { title: newTitle },
            config
        )
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error updating dashboard title:', error);
            throw error;
        });
};

export const updateDashboardLayout = async (req: RequestPatchDashboardLayout) => {
    return http
        .patch<RequestPatchDashboardLayout, ResponsePatchDashboardLayout>(
            `dashboard/${req.id}/dashboard-layout`,
            {
                widgetLayout: req.widgetLayout,
                widgets: req.widgets,
            }
        )
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error updating dashboard layout:', error);
            throw error;
        });
};

export const patchDashboardFavorite = async (req: RequestPatchDashboardFavorite) => {
    return http
        .patch<RequestPatchDashboardFavorite, ResponsePatchDashboardFavorite>(
            `dashboard/${req.dashboardId}/favorite`,
            { isFavorite: req.isFavorite }
        )
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error updating dashboard favorite:', error);
            throw error;
        });
};

export const patchDashboardSortOrder = async (req: RequestPatchDashboardSortOrder) => {
    return http
        .patch<RequestPatchDashboardSortOrder, ResponsePatchDashboardSortOrder>(
            `dashboard/sort-order`,
            { items: req.items }
        )
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error updating dashboard sort order:', error);
            throw error;
        });
};

export const deleteDashboard = async (req: RequestDeleteDashboard) => {
    const config: CustomAxiosRequestConfig = {
        errorMessage: {
            404: __('delete-dashboard-error-response-404', { dashboardId: req.id }),
        },
        successMessage: __('delete-dashboard-success'),
    };

    return http
        .delete<RequestDeleteDashboard, ResponseDeleteDashboard>(`dashboard/${req.id}`, config)
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error deleting dashboard:', error);
            throw error;
        });
};
